import { useState } from 'react'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import Link from '@mui/material/Link'

import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import palette from '../../theme/palette'
import { Visibility, VisibilityOff } from '@mui/icons-material'

export type loginForm = {
  email: string
  password: string
  rememberMe?: boolean
}

type loginProps = {
  onRegisterClicked: () => void
  onForgotPasswordClicked: () => void
  onSubmitLogin: (params: loginForm) => void
  isLoading: boolean
}

function LoginForm({
  onRegisterClicked,
  onForgotPasswordClicked,
  onSubmitLogin,
  isLoading,
}: loginProps) {
  const { t } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<loginForm>()

  const handleClickShowPassword = () => setShowPassword((show) => !show)

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const onSubmit: SubmitHandler<loginForm> = (data) => onSubmitLogin(data)

  return (
    <Card>
      <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" fontWeight="500" mb={2}>
          {t('security:login:submit')} Equimetre
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Email Field */}
            <TextField
              autoComplete="username"
              FormHelperTextProps={{ variant: 'standard', error: true }}
              helperText={errors.email?.message}
              error={Boolean(errors.email)}
              fullWidth
              label={t('info:email')}
              {...register('email', {
                required: {
                  value: true,
                  message: 'Email field is required',
                },
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Entered value does not match email format',
                },
              })}
            />

            {/* Password Field with Toggle Adornment */}
            <TextField
              autoComplete="current-password"
              FormHelperTextProps={{ variant: 'standard', error: true }}
              helperText={errors.password?.message}
              error={Boolean(errors.password)}
              fullWidth
              label={t('security:login:password')}
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...register('password', {
                required: {
                  value: true,
                  message: 'Password field is required',
                },
              })}
            />

            {/* Remember Me & Reset Password */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <FormControlLabel
                control={<Checkbox {...register('rememberMe')} defaultChecked />}
                label={
                  <Typography variant="body2">
                    {t('security:login:remember_me')}
                  </Typography>
                }
              />
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={onForgotPasswordClicked}
                sx={{
                  cursor: 'pointer',
                  color: (theme) => theme.palette.primary.light,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('button:resetPassword')}
              </Link>
            </Box>

            <LoadingButton loading={isLoading} variant="contained" type="submit">
              {t('security:login:submit')}
            </LoadingButton>
          </Box>
        </form>

        <Divider sx={{ my: 2, fontSize: 12 }} />

        <Button onClick={onRegisterClicked} fullWidth variant="outlined">
          {t('registration:submit')}
        </Button>
      </CardContent>
    </Card>
  )
}

export default LoginForm